import React from 'react';
import CreditCostPreview from '../../ui/CreditCostPreview';
import CustomSelect from '../../ui/CustomSelect';
import { ModeOption } from '../../../hooks/useModeRestrictions';
import { SubscriptionType } from '../../../services/subscriptionService';

interface SummarySidebarProps {
    inputImageUrls: string[];
    selectedModelUrl?: string | null;
    selectedModelName?: string | null;
    selectedBackgroundUrl?: string | null;
    selectedBackgroundName?: string | null;
    selectedPoses?: { id: string; type: string; gender?: string }[];
    mode: string;
    onModeChange: (mode: string) => void;
    availableModes: ModeOption[];
    prompt: string;
    onPromptChange: (prompt: string) => void;
    showAdvancedSettings: boolean;
    onToggleAdvanced: () => void;
    creditEstimation: {
        creditCost: number | null;
        isEstimating: boolean;
        error: string | null;
        canAfford: boolean;
    };
    onGenerate: () => void;
    isProcessing: boolean;
    className?: string;
    showPromptDirectly?: boolean;
    promptLabel?: string;
    promptPlaceholder?: string;
    hidePrompt?: boolean;
    generationTime?: number;
    enable4k?: boolean;
    onEnable4kChange?: (enabled: boolean) => void;
    showEnable4kCheckbox?: boolean;
}

export const SummarySidebar: React.FC<SummarySidebarProps> = ({
    inputImageUrls,
    selectedModelUrl,
    selectedModelName,
    selectedBackgroundUrl,
    selectedBackgroundName,
    selectedPoses,
    mode,
    onModeChange,
    availableModes,
    prompt,
    onPromptChange,
    showAdvancedSettings,
    onToggleAdvanced,
    creditEstimation,
    onGenerate,
    isProcessing,
    className = '',
    showPromptDirectly = false,
    promptLabel = 'Suffix Prompt (Optional)',
    promptPlaceholder = 'Add styling details...',
    hidePrompt = false,
    generationTime,
    enable4k = true,
    onEnable4kChange,
    showEnable4kCheckbox = false,
}) => {
    return (
        <div className={`bg-white rounded-2xl p-4 border border-gray-200 shadow-sm space-y-4 flex flex-col ${className}`}>
            {/* Input Images */}
            <div className="space-y-1.5">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Input</p>
                <div className="grid grid-cols-3 gap-2">
                    {inputImageUrls.map((url, idx) => (
                        <div key={idx} className="aspect-square bg-white rounded-lg border border-gray-100 overflow-hidden">
                            <img src={url} alt={`Input ${idx + 1}`} className="w-full h-full object-cover" />
                        </div>
                    ))}
                </div>
            </div>

            {/* Model Selection */}
            <div className="space-y-1.5">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Model</p>
                <div className="flex items-center space-x-3 bg-white p-2.5 rounded-xl border border-gray-100">
                    <div className="w-10 h-10 bg-gray-50 rounded-lg flex-shrink-0 border border-gray-50 overflow-hidden">
                        {selectedModelUrl ? (
                            <img src={selectedModelUrl} alt="Model" className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-300">
                                <i className="fas fa-user text-sm"></i>
                            </div>
                        )}
                    </div>
                    <p className="text-xs font-semibold text-gray-900 truncate">{selectedModelName || 'Not selected'}</p>
                </div>
            </div>

            {/* Poses (Optional) */}
            {selectedPoses && selectedPoses.length > 0 && (
                <div className="space-y-1.5">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Poses</p>
                    <div className="flex flex-wrap gap-1">
                        {selectedPoses.map((p, i) => (
                            <span key={p.id} className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-[10px] font-medium text-gray-750">
                                {i + 1}. {p.type} {p.gender ? `(${p.gender})` : ''}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Background Selection */}
            {selectedBackgroundName && (
                <div className="space-y-1.5">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Background</p>
                    <div className="flex items-center space-x-3 bg-white p-2.5 rounded-xl border border-gray-100">
                        <div className="w-10 h-10 bg-gray-50 rounded-lg flex-shrink-0 border border-gray-50 overflow-hidden">
                            {selectedBackgroundUrl ? (
                                <img src={selectedBackgroundUrl} alt="BG" className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-300">
                                    <i className={`fas ${selectedBackgroundName === 'No Change' ? 'fa-ban' : 'fa-image'} text-sm`}></i>
                                </div>
                            )}
                        </div>
                        <p className="text-xs font-semibold text-gray-900 truncate">{selectedBackgroundName || 'Not selected'}</p>
                    </div>
                </div>
            )}

            {/* Quality Mode */}
            <div className="space-y-1.5">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Quality Mode</p>
                <CustomSelect
                    value={mode}
                    onChange={onModeChange}
                    options={availableModes.map((mo) => ({
                        value: mo.value,
                        label: `${mo.label}${mo.requiresPlan?.[0] === SubscriptionType.PLUS ? ' (Plus+)' : mo.requiresPlan?.[0] === SubscriptionType.PRO ? ' (Pro+)' : ''}`,
                        disabled: mo.disabled,
                    }))}
                    className="w-full bg-white border border-gray-200 rounded-xl px-2.5 py-2 text-xs font-semibold text-gray-900"
                />
            </div>

            {/* Enable 4K HD Photos Checkbox */}
            {showEnable4kCheckbox && (
                <div className="pt-1.5 pb-0.5 flex items-center justify-between bg-gray-50/50 p-2.5 rounded-xl border border-gray-100 hover:border-secondary-200 transition-colors duration-200">
                    <label htmlFor="enable-4k-checkbox" className="flex flex-col cursor-pointer select-none pr-2">
                        <span className="text-xs font-bold text-gray-700">Enable 4K HD Photos</span>
                        <span className="text-[10px] text-gray-450 mt-0.5 leading-snug">Upscale output images to 4K resolution (+0.25 credits/image)</span>
                    </label>
                    <div className="relative flex items-center">
                        <input
                            type="checkbox"
                            id="enable-4k-checkbox"
                            checked={enable4k}
                            onChange={(e) => onEnable4kChange?.(e.target.checked)}
                            className="w-4 h-4 text-secondary-600 border-gray-300 rounded focus:ring-secondary-500 cursor-pointer accent-secondary-600 transition-all"
                        />
                    </div>
                </div>
            )}

            {/* Advanced Settings or Suffix Prompt */}
            {!hidePrompt && (
                showPromptDirectly ? (
                    <div className="space-y-3">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">{promptLabel}</p>
                        <textarea
                            value={prompt}
                            onChange={(e) => onPromptChange(e.target.value)}
                            placeholder={promptPlaceholder}
                            className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm min-h-[90px] focus:ring-secondary-500 focus:border-secondary-500 shadow-sm resize-none"
                        />
                    </div>
                ) : (
                    <div className="space-y-3">
                        <button onClick={onToggleAdvanced} className="flex items-center justify-between w-full text-xs font-bold text-gray-400 uppercase tracking-wider">
                            <span>Advanced Settings</span>
                            <i className={`fas fa-chevron-${showAdvancedSettings ? 'up' : 'down'} text-gray-450`}></i>
                        </button>
                        {showAdvancedSettings && (
                            <div className="pt-2">
                                <label className="block text-xs font-medium text-gray-600 mb-1">Prompt (Optional)</label>
                                <textarea
                                    value={prompt}
                                    onChange={(e) => onPromptChange(e.target.value)}
                                    placeholder="Add any specific instructions..."
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm min-h-[80px] resize-y focus:ring-secondary-500 focus:border-secondary-500"
                                />
                            </div>
                        )}
                    </div>
                )
            )}

            {/* Footer / Generate */}
            <div className="pt-3 border-t border-gray-100 space-y-3 mt-2">
                <CreditCostPreview
                    creditCost={creditEstimation.creditCost}
                    isEstimating={creditEstimation.isEstimating}
                    error={creditEstimation.error}
                    canAfford={creditEstimation.canAfford}
                />
                <button
                    onClick={onGenerate}
                    disabled={isProcessing}
                    className="w-full bg-secondary-600 text-white py-3 rounded-xl font-bold hover:bg-secondary-700 flex items-center justify-center space-x-2 disabled:bg-secondary-400"
                >
                    {isProcessing ? (
                        <div className="flex items-center justify-center space-x-2">
                            <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></span>
                            {generationTime !== undefined && (
                                <span className="font-mono text-sm">{generationTime.toFixed(1)}s</span>
                            )}
                        </div>
                    ) : (
                        <>
                            <i className="fas fa-wand-magic-sparkles text-sm"></i>
                            <span>Generate</span>
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};
